import dayjs from 'dayjs';
import { setReviewField } from 'feature/claims/claimSlice';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';
import { DatePicker, FormControl, Stack, TextField, Typography } from 'styles';
import { REVIEW_FIELDS } from 'types/Claim';
import { TRANSLATION_KEYS } from 'utils/constants';
import { useTranslation } from 'utils/translations';
import { formatPrice, formatZuluForBE, getNumberFromString } from 'utils/utils';

const Invoice = () => {
	const isFirstRender = useRef(true);
	const dispatch = useDispatch<AppDispatch>();
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: TRANSLATION_KEYS.CLIENT_LABELS,
	});

	const { claimData } = useSelector((store: RootState) => store.claim);

	const [totalInvoiceAmount, setTotalInvoiceAmount] = useState<string>(
		claimData.receipt[REVIEW_FIELDS.TOTAL_INVOICE_AMOUNT]
			? formatPrice(claimData.receipt[REVIEW_FIELDS.TOTAL_INVOICE_AMOUNT])
			: '',
	);

	const [retailNetOfTax, setRetailNetOfTax] = useState<string>(
		claimData.receipt[REVIEW_FIELDS.RETAIL_NET_OF_TAX]
			? formatPrice(claimData.receipt[REVIEW_FIELDS.RETAIL_NET_OF_TAX])
			: '',
	);

	const handleChange = (name: REVIEW_FIELDS, value: string | number) => {
		dispatch(setReviewField({ name, value }));
	};

	useEffect(() => {
		if (isFirstRender.current) {
			setTotalInvoiceAmount(
				formatPrice(claimData.receipt[REVIEW_FIELDS.TOTAL_INVOICE_AMOUNT]),
			);
			setRetailNetOfTax(
				formatPrice(claimData.receipt[REVIEW_FIELDS.RETAIL_NET_OF_TAX]),
			);
		}

		// basically wait for the data to be fetched if the user reloads this page
		// check that is not the first render
		// or the value of the data is not the default one
		if (
			!isFirstRender.current ||
			(claimData.receipt[REVIEW_FIELDS.TOTAL_INVOICE_AMOUNT] !== '' &&
				claimData.receipt[REVIEW_FIELDS.RETAIL_NET_OF_TAX] !== '')
		) {
			isFirstRender.current = false;
		}
	}, [claimData.receipt]);

	return (
		<Stack gap={'25px'}>
			<Typography variant="title6">
				{tClientLabels('claim.review.invoice')}
			</Typography>
			<Stack direction={'row'} gap={'10px'}>
				<FormControl fullWidth>
					<TextField
						name={REVIEW_FIELDS.INVOICE_NUMBER}
						type="text"
						label={tClientLabels('claim.review.invoiceNumber')}
						onChange={e => handleChange(REVIEW_FIELDS.INVOICE_NUMBER, e.target.value)}
						autoComplete={'off'}
						required
						value={claimData.receipt[REVIEW_FIELDS.INVOICE_NUMBER]}
					/>
				</FormControl>
				<FormControl fullWidth>
					<DatePicker
						name={REVIEW_FIELDS.INVOICE_DATE}
						required
						variant="margin"
						label={tClientLabels('claim.review.invoiceDate')}
						value={dayjs(claimData.receipt[REVIEW_FIELDS.INVOICE_DATE]) ?? null}
						onChange={value =>
							handleChange(REVIEW_FIELDS.INVOICE_DATE, formatZuluForBE(value))
						}
					/>
				</FormControl>
				<FormControl fullWidth>
					<TextField
						name={REVIEW_FIELDS.BILLING_ENTITY_NAME}
						type="text"
						label={tClientLabels('claim.review.billingEntityName')}
						onChange={e =>
							handleChange(REVIEW_FIELDS.BILLING_ENTITY_NAME, e.target.value)
						}
						autoComplete={'off'}
						required
						value={claimData.receipt[REVIEW_FIELDS.BILLING_ENTITY_NAME]}
					/>
				</FormControl>
				<FormControl fullWidth>
					<TextField
						name={REVIEW_FIELDS.VAT_NUMBER}
						type="text"
						label={tClientLabels('claim.review.VATNumber')}
						onChange={e => handleChange(REVIEW_FIELDS.VAT_NUMBER, e.target.value)}
						autoComplete={'off'}
						required
						value={claimData.receipt[REVIEW_FIELDS.VAT_NUMBER]}
					/>
				</FormControl>
				<FormControl fullWidth>
					<TextField
						name={REVIEW_FIELDS.TOTAL_INVOICE_AMOUNT}
						type="text"
						label={tClientLabels('claim.review.totalInvoiceAmount')}
						onChange={e => {
							handleChange(
								REVIEW_FIELDS.TOTAL_INVOICE_AMOUNT,
								getNumberFromString(e.target.value),
							);
							setTotalInvoiceAmount(e.target.value);
						}}
						onBlur={e => {
							setTotalInvoiceAmount(formatPrice(getNumberFromString(e.target.value)));
							handleChange(
								REVIEW_FIELDS.TOTAL_INVOICE_AMOUNT,
								getNumberFromString(formatPrice(getNumberFromString(e.target.value))),
							);
						}}
						autoComplete={'off'}
						required
						value={totalInvoiceAmount}
					/>
				</FormControl>
				<FormControl fullWidth>
					<TextField
						name={REVIEW_FIELDS.RETAIL_NET_OF_TAX}
						type="text"
						label={tClientLabels('claim.review.retailNetOfTax')}
						onChange={e => {
							handleChange(
								REVIEW_FIELDS.RETAIL_NET_OF_TAX,
								getNumberFromString(e.target.value),
							);
							setRetailNetOfTax(e.target.value);
						}}
						onBlur={e => {
							setRetailNetOfTax(formatPrice(getNumberFromString(e.target.value)));
							handleChange(
								REVIEW_FIELDS.RETAIL_NET_OF_TAX,
								getNumberFromString(formatPrice(getNumberFromString(e.target.value))),
							);
						}}
						autoComplete={'off'}
						required
						value={retailNetOfTax}
					/>
				</FormControl>
			</Stack>
		</Stack>
	);
};

export default Invoice;
